/** The one helper for every state-changing request the SPA makes.
 *
 *  The daemon enforces CSRF on every POST/PUT/PATCH/DELETE, so a mutation that
 *  goes out through plain `fetch` fails with 403. Everything that changes state
 *  comes through here, which:
 *
 *    - attaches `X-CSRF-Token` from the current session,
 *    - on a 403 `csrf_token_required` / `csrf_token_mismatch`, refreshes the
 *      session from /auth/me and retries exactly once with the new token,
 *    - honours the CALLER's AbortSignal and a timeout at the same time.
 *
 *  That last point is the fix for a real defect: there used to be two copies of
 *  this helper. The one in lib/auth.ts replaced the caller's signal with its
 *  own, so the mail view's 5 s and 60 s action timeouts never fired and a
 *  wedged bridge call spun forever; the calendar's copy had no timeout at all.
 *  Now the two signals are composed and either one ends the request.
 *
 *  Timeouts default to {@link DEFAULT_MUTATION_TIMEOUT_MS}. Pass
 *  `{ timeoutMs: null }` for the rare call that must be left to the browser.
 *  A timeout rejects with {@link CsrfFetchTimeoutError}; a caller abort rejects
 *  with the usual AbortError, so callers can tell "I cancelled" from "it hung". */

import { getCsrfToken, refreshAuth } from "./authState.js";
import { anySignal } from "./signals.js";

export const DEFAULT_MUTATION_TIMEOUT_MS = 60_000;

/** Thrown when a csrfFetch call exceeds its timeoutMs. Callers can
 *  `instanceof`-check this to render a "bridge did not respond" message
 *  instead of a generic abort string. */
export class CsrfFetchTimeoutError extends Error {
  constructor(public readonly timeoutMs: number) {
    super(`request timed out after ${timeoutMs}ms`);
    this.name = "CsrfFetchTimeoutError";
  }
}

export type CsrfFetchOptions = {
  /** Per-attempt timeout. `undefined` = the default, `null` or 0 = none. */
  timeoutMs?: number | null;
};

function bodyIsJsonish(body: RequestInit["body"]): boolean {
  if (body === undefined || body === null) return true;
  if (typeof body === "string") return true;
  return false;
}

export async function csrfFetch(
  url: string,
  init: RequestInit = {},
  opts: CsrfFetchOptions = {},
): Promise<Response> {
  const timeoutMs = opts.timeoutMs === undefined ? DEFAULT_MUTATION_TIMEOUT_MS : opts.timeoutMs;
  const callerSignal = init.signal ?? undefined;

  const buildHeaders = (): Headers => {
    const headers = new Headers(init.headers ?? undefined);
    if (!headers.has("Content-Type") && bodyIsJsonish(init.body)) {
      headers.set("Content-Type", "application/json");
    }
    const csrf = getCsrfToken();
    if (csrf) headers.set("X-CSRF-Token", csrf);
    return headers;
  };

  const fire = async (): Promise<Response> => {
    if (callerSignal?.aborted) {
      throw callerSignal.reason ?? new DOMException("The operation was aborted.", "AbortError");
    }
    const timeoutCtrl = new AbortController();
    let timedOut = false;
    const timer = timeoutMs && timeoutMs > 0
      ? setTimeout(() => { timedOut = true; timeoutCtrl.abort(); }, timeoutMs)
      : null;
    try {
      return await fetch(url, {
        credentials: "same-origin",
        ...init,
        headers: buildHeaders(),
        signal: anySignal([callerSignal, timeoutCtrl.signal]),
      });
    } catch (e) {
      if (timedOut && !callerSignal?.aborted) throw new CsrfFetchTimeoutError(timeoutMs as number);
      throw e;
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  const r1 = await fire();
  if (r1.status !== 403) return r1;
  let isCsrfError = false;
  try {
    const text = await r1.clone().text();
    isCsrfError = /csrf_token_(required|mismatch)/.test(text);
  } catch {
    /* fall through; first response is final */
  }
  if (!isCsrfError) return r1;
  try {
    await refreshAuth();
  } catch {
    return r1;
  }
  return fire();
}
